import { makeAutoObservable, runInAction } from 'mobx';
import { IChangeable } from '../../../stores/types';
import { ISlot, ITemplate } from '../emailContentTypes';
import TemplateStore from './template-store';

interface ISlotChanges extends Partial<ISlot> {}

class SlotsStore implements IChangeable<ISlot> {
	private _changes: ISlotChanges = {};
	private _model?: ISlot | null;

	/** Sub-stores */
	private _templateStores?: TemplateStore[];

	constructor(model?: ISlot | null) {
		this._setModel(model);
		makeAutoObservable(this);
	}

	getUpdatedModel(): ISlot {
		const hasLocalChanges = !!Object.keys(this._changes).length;

		return {
			...this._model,
			...this._changes,
			templates: this._templateStores?.map<ITemplate>((ts) => ts.getUpdatedModel()),
			_has_changes: hasLocalChanges,
		};
	}

	public get deleted() {
		return this._changes._deleted ?? false;
	}
	public get name() {
		return this._changes.name ?? this._model?.name;
	}

	public getContent(locale: string) {
		const store = this._templateStores?.find((t) => t.locale === (locale ?? 'en'));
		return store?.content;
	}

	public setContent(locale: string, value: string | null) {
		this._templateStores?.find((t) => t.locale === (locale ?? 'en'))?.setContent(value);
	}

	public setChanges(slot: ISlotChanges) {
		this._changes = slot;

		this._templateStores?.forEach((t) => {
			const template = slot.templates?.find((template) => template.locale === t.locale);
			t.setChanges(template ?? ({} as ITemplate));
		});
	}

	hasChanges(): boolean {
		const templatesChanges = this._templateStores?.map((ts) => ts.hasChanges());

		return (
			!!Object.keys(this._changes).length ||
			!!templatesChanges?.reduce((accumulator, currentValue) => accumulator || currentValue, false)
		);
	}

	discardChanges(): void {
		this._changes = {} as ISlotChanges;
		this._templateStores?.forEach((ts) => ts.discardChanges());
	}

	public appendTemplateStore(content: string | null, locale: string): TemplateStore {
		const changes = {
			locale: locale,
			content: content,
		};
		return runInAction(() => {
			const templateStore = new TemplateStore();
			templateStore.setChanges(changes);
			this._templateStores?.push(templateStore);

			return templateStore;
		});
	}

	private _setModel(model: ISlot | undefined | null): void {
		this._model = model;
		this._changes = {} as ISlotChanges;

		this._templateStores = model?.templates?.map((template) => new TemplateStore(template)) ?? [];
	}
}

export default SlotsStore;
